import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  CreateCompositeShipmentRequest,
  FilteringPendingShipments,
  ListPendingShipmentsRequest,
  PendingShipment,
} from '~api/som';

import { SomApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { fetchShippingMethodList } from '~src/main/queries';
import { fetchTransportOrderDetails } from '~src/pages/order/queries';
import { ShipmentUpsert } from '~src/pages/shipment/types';
import { createContactFromAddress } from '~src/utils/create-contact-from-address';

import { fetchCarrierContract } from '../carrier-contract/queries';

export type PendingShipmentListItem = Exclude<ReturnType<typeof usePendingShipmentListQuery>['data'], undefined>[0];

type PendingShipmentListQueryParams = Omit<ListPendingShipmentsRequest, 'pagination'> & {
  pageIndex?: number;
  pageSize?: number;
};

export function usePendingShipmentQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['pending-shipments', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (params: PendingShipmentListQueryParams) => [...keys.lists(), params] as const,
    count: (params: FilteringPendingShipments) => [...keys.lists(), 'count', params],
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export const fetchPendingShipments = async (params: PendingShipmentListQueryParams) => {
  const { pageIndex, pageSize, ...query } = params;

  const { data } = await SomApiInstance.pendingShipmentsList.somListPendingShipments({
    pagination: {
      offset: (pageIndex ?? 0) * (pageSize ?? 10),
      limit: pageSize ?? 10,
    },
    ...query,
  });

  return (data.pending_shipments ?? []).map(pendingShipment => ({
    ...pendingShipment,
    payload: JSON.parse(pendingShipment.payload ?? '{}') as CreateCompositeShipmentRequest,
  }));
};

export function usePendingShipmentListQuery(params: PendingShipmentListQueryParams = {}) {
  const keys = usePendingShipmentQueryKey();

  return useQuery({
    queryFn: async () => await fetchPendingShipments(params),
    queryKey: keys.list(params),
  });
}

export async function fetchPendingShipment({ lookup_id }: Pick<PendingShipment, 'lookup_id'>) {
  const { data } = await SomApiInstance.pendingShipmentsGet.somGetPendingShipment({ lookup_id });

  return {
    ...data.pending_shipment,
    payload: JSON.parse(data.pending_shipment?.payload ?? '') as CreateCompositeShipmentRequest,
  };
}

export function usePendingShipmentUpsertQuery({ lookup_id }: Pick<PendingShipment, 'lookup_id'>) {
  const keys = usePendingShipmentQueryKey();
  const siteId = useSiteId();

  return useSuspenseQuery({
    queryFn: async () => {
      const shippingMethods = await fetchShippingMethodList(siteId!, { customBookingMethods: true });
      const pendingShipment = await fetchPendingShipment({ lookup_id });

      const carrierContract = pendingShipment.payload.carrier_contract_id
        ? await fetchCarrierContract(pendingShipment.payload.carrier_contract_id)
        : undefined;

      const tos = pendingShipment.payload.tos_id
        ? await fetchTransportOrderDetails({ id: pendingShipment.payload.tos_id })
        : undefined;

      const upsertShipment: ShipmentUpsert = {
        ...pendingShipment.payload,
        tos,
        contact_from: createContactFromAddress('shipment', 'contact_from', pendingShipment.payload.address_from),
        contact_to: createContactFromAddress('shipment', 'contact_to', pendingShipment.payload.address_to),
        contact_return: createContactFromAddress('shipment', 'contact_return', pendingShipment.payload.address_return),
        customer_info: pendingShipment.payload.customer_info && {
          ...pendingShipment.payload.customer_info,
          contact: createContactFromAddress(
            'shipment',
            'contact_customer_info',
            pendingShipment.payload.customer_info?.address,
            pendingShipment.payload.customer_info?.phone,
            pendingShipment.payload.customer_info?.email
          ),
        },
        shipping_method: shippingMethods.find(s => s.id === pendingShipment.payload.shipping_method),
        carrier_contract: carrierContract
          ? {
              ...carrierContract,
              carrier_configuration_data: JSON.stringify(carrierContract.carrier_configuration_data),
            }
          : undefined,
        pending_shipment_id: pendingShipment.lookup_id,
      };

      return {
        error: pendingShipment.raw_error_message,
        upsertShipment,
      };
    },
    queryKey: keys.detail(lookup_id),
  });
}

export function usePendingShipmentCountQuery(params: FilteringPendingShipments = {}, throwOnError = true) {
  const keys = usePendingShipmentQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await SomApiInstance.pendingShipmentsCount.somCountPendingShipments({
        filtering: params,
      });

      return data;
    },
    queryKey: keys.count(params),
    throwOnError,
  });
}

export function useSafePendingShipmentCountQuery(params: FilteringPendingShipments = {}) {
  return usePendingShipmentCountQuery(params, false);
}
